import { Injectable } from '@angular/core';
import * as fromAdmin from '@app/client/account/admin/reducers';
import { IamService } from '@app/core/services/iam.service';
import * as fromRoot from '@app/reducers';
import { User } from '@app/shared/models/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { ProvisioningActions } from '../actions';

@Injectable()
export class ProvisioningEffects {

  loadProvisioningStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.LoadProvisioningStatus),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      concatMap(([action, identity]: any[]) => {
        return this.iamService.getProvisioningStatus(identity).pipe(
          concatMap((provisioningStatus) => {
            return of(ProvisioningActions.LoadProvisioningStatusSuccess({ active: provisioningStatus }));
          }),
        )
      }))
  );

  switchProvisioningStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.SwitchProvisioningStatus),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      concatMap(([action, identity]) => {
        return this.iamService.getProvisioningStatus(identity).pipe(
          concatMap((provisioningStatus) => {
            if (provisioningStatus) {
              return this.iamService.disableProvisioning(identity).pipe(concatMap(({ user }) => {
                return of(ProvisioningActions.SwitchProvisioningStatusSuccess({ active: user.status === Constant.userStatus.enabled }))
              }))
            } else {
              return this.iamService.enableProvisioning(identity).pipe(concatMap(({ user }) => {
                return of(ProvisioningActions.SwitchProvisioningStatusSuccess({ active: user.status === Constant.userStatus.enabled }))
              }))
            }
          }),
        )
      }))
  );

  generateSCIMToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.GenerateSCIMToken),
      withLatestFrom(this.store$.pipe(select(fromRoot.getActiveIdentity))),
      switchMap(([action, identity]) => {
        return this.iamService.generateSCIMToken(identity).pipe(map((user: User) => {
          return ProvisioningActions.GenerateSCIMTokenSuccess({ token: user.token.token })
        }))
      }),
      catchError(error => of(ProvisioningActions.GenerateSCIMTokenFailure({ error: error.message }))))
  );

  resetProvisioning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.ResetProvisioning),
      switchMap(async () => {
        try {
          const { groups } = await this.iamService.listGroups().toPromise();
          const SCIMGroups = groups.filter(group => group.provisioned);
          for (const group of SCIMGroups) {
            const [{ teams }, { users }, { roles }] = await Promise.all([
              this.iamService.listGroupTeams(group.id).toPromise(),
              this.iamService.listGroupUsers(group.id).toPromise(),
              this.iamService.listGroupRoles(group.id).toPromise(),
            ]);
            const reqsGroup = [
              ...users.map(user => this.iamService.detachUserFromGroup(group.id, user.id).toPromise()),
              ...roles.map(role => this.iamService.detachRoleFromGroup(group.id, role.name).toPromise()),
              ...teams.map(team => this.iamService.detachTeamFromGroup(group.id, team.id).toPromise()),
            ];
            await Promise.all(reqsGroup);
            await this.iamService.deleteGroup(group.id).toPromise();
          }
          const { users: accountUsers } = await this.iamService.listUsers().toPromise();
          const SCIMUsers = accountUsers.filter(user => user.provisioned);
          for (const user of SCIMUsers) {
            const [{ teams }, { roles }] = await Promise.all([
              this.iamService.listUserTeams(user.id).toPromise(),
              this.iamService.listUserRoles(user.id).toPromise(),
            ]);
            const reqsUser = [
              ...roles.map(role => this.iamService.detachUserRole(user.id, role.name).toPromise()),
              ...teams.map(team => this.iamService.detachUserFromTeam(team.id, user.id).toPromise()),
            ];
            await Promise.all(reqsUser);
            await this.iamService.deleteUser(user.id).toPromise();
          }
          return ProvisioningActions.ResetProvisioningSuccess();
        } catch (error) {
          return ProvisioningActions.ResetProvisioningFailure({ error });
        }
      }))
  );

  listDisabledUsersToDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.ListDisabledUsersToDelete),
      switchMap(async () => {
        try {
          const { users: accountUsers } = await this.iamService.listUsers().toPromise();
          const SCIMUsersToDelete = accountUsers.filter(user => user.provisioned && user.status === "Disabled");
          return ProvisioningActions.ListDisabledUsersToDeleteSuccess({ disabledUsersToDelete: SCIMUsersToDelete.length });
        } catch (error) {
          return ProvisioningActions.ListDisabledUsersToDeleteFailure({ error });
        }

      })));

  deleteDisabledUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProvisioningActions.DeleteDisabledUsers),
      switchMap(async () => {
        try {
          const { users: accountUsers } = await this.iamService.listUsers().toPromise();
          const SCIMUsersToDelete = accountUsers.filter(user => user.provisioned && user.status === "Disabled");
          for (const user of SCIMUsersToDelete) {
            const [{ teams }, { roles }, { groups }] = await Promise.all([
              this.iamService.listUserTeams(user.id).toPromise(),
              this.iamService.listUserRoles(user.id).toPromise(),
              this.iamService.listUserGroups(user.id).toPromise()
            ]);
            const reqsUser = [
              ...roles.map(role => this.iamService.detachUserRole(user.id, role.name).toPromise()),
              ...teams.map(team => this.iamService.detachUserFromTeam(team.id, user.id).toPromise()),
              ...groups.map(group => this.iamService.detachUserFromGroup(group.id, user.id).toPromise())
            ];
            await Promise.all(reqsUser);
            await this.iamService.deleteUser(user.id).toPromise();
          }
          return ProvisioningActions.DeleteDisabledUsersSuccess({ deletedUserCount: SCIMUsersToDelete.length });
        } catch (error) {
          return ProvisioningActions.DeleteDisabledUsersFailure({ error: error.error.error, code: error.error.code });
        }

      })));

  constructor(
    private actions$: Actions,
    private iamService: IamService,
    private store$: Store<fromRoot.State & fromAdmin.State>
  ) { }

}
